import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ShieldOff, Timer } from 'lucide-react'
import { useState } from 'react'
import { AButton, Callout, Chip, Panel } from './Section'
import { api, get } from '../../lib/api'

/**
 * Clearing a rate limit — the help desk's other unlock button.
 *
 * Two different things produce "I can't sign in": an account lockout after
 * five wrong passwords, and a rate limit after too many attempts. They look
 * identical to the person affected, but only the first had a remedy — the
 * second could only be cleared by restarting the server, which is not an
 * option at 09:15 with a queue at the registration desk.
 *
 * Found the hard way: a test run tripped a limit on one account and the only
 * way back was a restart.
 */

interface Limited {
  limit: string
  key: string
  hits: number
  max: number
}

/** What each limit means in words the help desk can use. */
const MEANS: Record<string, string> = {
  register: 'tried to register too many times',
  resendOtp: 'asked for too many new codes',
  changeEmail: 'changed e-mail address too many times',
  verifyOtp: 'entered a wrong code too many times',
  studentLogin: 'tried to reopen their pass too many times',
  staffLogin: 'too many wrong passwords on this account',
  staffLoginIp: 'too many wrong passwords from this device',
  verifyCertificate: 'checked too many certificates',
  boothScan: 'scanned faster than the limit allows',
}

export function RateLimitHelpDesk({ className }: { className?: string }) {
  const queryClient = useQueryClient()
  const [subject, setSubject] = useState('')
  const [notice, setNotice] = useState<string | null>(null)

  const limited = useQuery({
    queryKey: ['admin-rate-limits'],
    queryFn: () => get<{ limited: Limited[] }>('/api/admin/rate-limits', 'staff'),
    refetchInterval: 10_000,
  })

  const clear = useMutation({
    mutationFn: (value: string) =>
      api<{ cleared: number }>('/api/admin/rate-limits/clear', {
        method: 'POST',
        body: JSON.stringify({ subject: value }),
        auth: 'staff',
      }),
    onSuccess: (res, value) => {
      setNotice(
        res.cleared > 0
          ? `Cleared ${res.cleared} limit${res.cleared > 1 ? 's' : ''} for ${value}. They can try again now.`
          : `Nothing was limited for ${value}. The problem is something else — check the account lockout above.`,
      )
      setSubject('')
      queryClient.invalidateQueries({ queryKey: ['admin-rate-limits'] })
      queryClient.invalidateQueries({ queryKey: ['admin-accounts'] })
    },
    onError: () => setNotice('Could not clear that limit.'),
  })

  const rows = limited.data?.limited ?? []

  // Nothing is limited and nobody is asking: stay out of the way.
  if (rows.length === 0 && !notice && !subject) return null

  return (
    <Panel
      title="Rate limits"
      description={
        rows.length > 0
          ? `${rows.length} currently blocked from trying again`
          : 'Nothing is currently rate limited'
      }
      className={className}
    >
      {notice && (
        <Callout tone={notice.startsWith('Could not') ? 'critical' : 'neutral'} className="mb-3">
          {notice}
        </Callout>
      )}

      {rows.length > 0 && (
        <ul className="mb-3 space-y-1.5">
          {rows.map((r) => (
            <li
              key={`${r.limit}:${r.key}`}
              className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2"
            >
              <Timer className="size-4 shrink-0 text-amber-600" strokeWidth={2.2} />
              <span className="min-w-0 flex-1">
                <span className="block break-all text-sm font-semibold text-[#0B1E3F]">
                  {r.key}
                </span>
                <span className="block text-xs text-slate-500">
                  {MEANS[r.limit] ?? r.limit}
                </span>
              </span>
              <Chip tone="attention">
                {r.hits}/{r.max}
              </Chip>
              <AButton
                size="sm"
                tone="outline"
                disabled={clear.isPending}
                onClick={() => {
                  setNotice(null)
                  clear.mutate(r.key)
                }}
              >
                <ShieldOff className="size-3.5" strokeWidth={2.2} />
                Clear
              </AButton>
            </li>
          ))}
        </ul>
      )}

      {/* Also searchable: a student at the desk gives their e-mail, not a key. */}
      <div className="flex flex-col gap-2 sm:flex-row">
        <input
          value={subject}
          onChange={(e) => {
            setSubject(e.target.value)
            setNotice(null)
          }}
          placeholder="E-mail, username or address to clear"
          className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
        />
        <AButton
          tone="navy"
          className="sm:w-auto"
          block
          disabled={!subject.trim() || clear.isPending}
          onClick={() => clear.mutate(subject.trim())}
        >
          {clear.isPending ? 'Clearing…' : 'Clear limit'}
        </AButton>
      </div>
    </Panel>
  )
}
